import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { i18n } from "../lib/i18n";

// Connects to data-controller="auto-save"
export default class extends Controller<HTMLFormElement> {
  static targets = ["form", "errorContainer"];
  static values = {
    debounce: { type: Number, default: 500 },
    turboFormSubmit: { type: Boolean, default: true },
    acceptTurboStream: { type: Boolean, default: false }
  };

  declare debounceValue: number | undefined;
  declare timeout: NodeJS.Timeout | undefined;
  declare formTarget?: HTMLFormElement;
  declare hasFormTarget: boolean;
  declare errorContainerTarget?: HTMLElement;
  declare hasErrorContainerTarget: boolean;
  declare turboFormSubmitValue: boolean;
  declare acceptTurboStreamValue: boolean;

  connect() {
    if (!this.turboFormSubmitValue) {
      this.formObject().addEventListener("submit", (event) =>
        this.submitForm(event, true),
      );
    }
  }

  disconnect() {
    if (!this.turboFormSubmitValue) {
      this.formObject().removeEventListener("submit", (event) =>
        this.submitForm(event, true),
      );
    }
  }

  formObject(): HTMLFormElement {
    return this.hasFormTarget ? this.formTarget : this.element;
  }

  submitForm(event: Event, preventDefault: boolean = false) {
    if (preventDefault) {
      event.preventDefault();
    }

    clearTimeout(this.timeout);

    const version = this.pendingSave();
    this.timeout = setTimeout(() => this.sendForm(version), this.debounceValue);
  }

  // Flush a pending debounced submit immediately. Use when the user is about
  // to navigate away (e.g. opening a sub-page in the drawer) and we can't
  // afford to wait for the debounce — once the frame is replaced the form is
  // detached and the in-flight setTimeout may race the new render.
  flush() {
    clearTimeout(this.timeout);
    this.sendForm(this.pendingSave());
  }

  // The save in flight for this form; the next one waits for it.
  private inflight: Promise<void> = Promise.resolve();

  // Saves for one form go out in order and read the form as they go: a slower
  // earlier request can never overtake a later one and leave an older value
  // last, and a burst of changes collapses into the newest state.
  private saveVersion = 0;

  private pendingSave(): number {
    this.formObject().dispatchEvent(new CustomEvent("auto-save:pending", { bubbles: true }));
    return ++this.saveVersion;
  }

  private sendForm(version: number): Promise<void> {
    const send = async () => {
      try {
        await (this.acceptTurboStreamValue ? this.sendFormForStream() : this.sendFormForJson());
      } finally {
        if (version === this.saveVersion) {
          this.formObject().dispatchEvent(new CustomEvent("auto-save:finished", { bubbles: true }));
        }
      }
    };
    this.inflight = this.inflight.then(send, send);
    return this.inflight;
  }

  private async sendFormForJson() {
    const form = this.formObject();
    const formData = new FormData(form);

    const response = await fetch(form.action, {
      method: form.method,
      body: formData,
      headers: {
        Accept: "application/json",
        ...csrfHeader()
      },
      credentials: "same-origin"
    });

    if (response.status === 204) return;

    const data = await response.json();

    if (this.hasErrorContainerTarget) {
      this.updateErrorContainer(data.errors);
    } else {
      const type = response.ok || data.status === "ok" ? "success" : "error";
      const message = data.message || i18n.t('auto_save.error_saving');
      const event = new CustomEvent("toast", {
        detail: {
          type,
          message
        }
      });
      window.dispatchEvent(event);
    }
  }

  // Opt-in: let the server re-render the record's frame instead of answering
  // with JSON. Controls that paint their own display in JS (a picker label, a
  // comment mirror) otherwise keep showing values the server rejected.
  // Opting in requires the action to have a .turbo_stream.erb — `success_response`
  // has no MissingTemplate rescue, so a successful save would 500 without one.
  //
  // The server decides when the row is out of date and answers with a stream
  // only then — a rejected save, or one that stored something the form did not
  // send, like a size that followed a line to its new recipient. An ordinary
  // save answers empty and the DOM is left alone, because re-rendering the
  // frame would tear out whatever field the user is still typing in.
  //
  // Status is deliberately not the signal: a successful save can leave the row
  // just as stale as a rejected one.
  private async sendFormForStream() {
    const form = this.formObject();
    const formData = new FormData(form);

    const response = await fetch(form.action, {
      method: form.method,
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        ...csrfHeader()
      },
      credentials: "same-origin"
    });

    if (response.status === 204) return;

    const html = await response.text();
    if (html.trim()) Turbo.renderStreamMessage(html);
  }

  updateErrorContainer(errors: string | undefined) {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget!.innerHTML = errors || "";
    }
  }
}

// The page's CSRF token, as Turbo sends it. A form rendered outside a request
// (a Turbo Stream broadcast from a job) carries an authenticity_token no
// session knows; the header from the layout's meta tag is always the real one.
function csrfHeader(): Record<string, string> {
  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
  return token ? { "X-CSRF-Token": token } : {};
}
